import { type FC, useEffect, useState } from 'react';
import { DatePicker, Modal, Space, InputNumber, InputText, Select } from '@g2u/ui-kit';
import { isEmpty } from 'lodash';
import { Form } from 'antd';

import type { Product, ServiceProduct, WithOptionalDataTest } from 'types';
import { validateFormSilently } from 'helpers';
import styles from './add-service-modal.module.scss';
import Big from 'big.js';
import { calculateTotalHours } from 'helpers/hooks/useTotalHours';

type SelectedProduct = Partial<ServiceProduct> & { productId: ServiceProduct['productId'] };

type Props = {
  isOpen: boolean;
  defaultCompletionDate?: Date;
  selectedProducts?: SelectedProduct[];
  currentProductId: number | null;
  productList: Product[] | undefined;
  onOk: (product: {
    id?: number;
    productId: number;
    qty: number;
    completionDate: Date;
    details: {
      product?: Product;
    };
  }) => void;
  onCancel: () => void;
  isGeekcoverMember: boolean;
} & WithOptionalDataTest;

type FormFields = {
  productId: number;
  productQty?: number;
  productPrice: string;
  productDuration: number;
  productDate: Date;
};

export const AddServiceModal: FC<Props> = (props) => {
  const {
    isOpen,
    onOk,
    onCancel,
    selectedProducts,
    defaultCompletionDate,
    currentProductId: productIndex,
    productList,
    dataTestId,
    isGeekcoverMember,
  } = props;
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [form] = Form.useForm<FormFields>();
  const qty = Form.useWatch('productQty', form);
  const isNew = productIndex === null;

  const selectedProductId = Form.useWatch('productId', form);
  const selectedProduct = productList?.find((p) => p.ID === selectedProductId);

  useEffect(() => {
    if (productIndex !== null) {
      const editingProduct = selectedProducts?.[productIndex];
      form.setFieldsValue({
        productId: editingProduct?.productId,
        productQty: editingProduct?.quantity,
        productDate: editingProduct?.completionDate ?? defaultCompletionDate ?? new Date(),
      });
    }
  }, [productIndex, selectedProducts]);

  const handleFormSubmit = (formData: FormFields) => {
    onOk({
      id: productIndex === null ? undefined : selectedProducts?.[productIndex].id,
      productId: selectedProductId,
      qty: Big(formData?.productQty || 0).toNumber(),
      completionDate: formData.productDate,
      details: {
        product: selectedProduct,
      },
    });
  };

  //the value that represents the item that is ommitted for GC cusotmers is 13
  const additional30MinChargeHome = 13;

  const handleFormChange = () => {
    void (!isEmpty(form.getFieldsValue()) && validateFormSilently(form, setIsFormValid));
  };

  useEffect(() => {
    if (selectedProductId && selectedProduct) {
      const quantity = form.getFieldValue('productQty');
      form.setFieldsValue({
        productPrice: selectedProduct ? `$${Big(selectedProduct.total).toFixed(2)} inc GST` : '',
        productDuration: calculateTotalHours([{ details: { product: selectedProduct }, qty: quantity }]),
      });
    }
  }, [selectedProductId, selectedProduct, qty]);

  return (
    <Modal.$
      open={isOpen}
      maskClosable={false}
      title={isNew ? 'Add New Service' : ' Edit Service'}
      okText={isNew ? 'Add' : 'Save'}
      onOk={() => {
        form.submit();
      }}
      onCancel={onCancel}
      okButtonProps={{
        loading: false,
        disabled: !isFormValid,
      }}
      dataTestId={dataTestId ?? 'add-service-modal'}
    >
      <Form
        form={form}
        name="add-service-product-form"
        layout="vertical"
        onValuesChange={handleFormChange}
        onFinish={handleFormSubmit}
        colon={false}
        preserve={true}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Small} fullWidth>
          <Form.Item>
            <div>
              <div className={styles.serviceList__item}>
                <Select.$
                  disabled={!isNew}
                  width={384}
                  style={{ width: '100%', marginRight: 8 }}
                  placeholder="Select a product"
                  optionFilterProp="children"
                  className={styles.serviceList__select}
                  showSearch
                  name="productId"
                  initialValue={
                    productIndex === null ? undefined : selectedProducts?.[productIndex]?.productId.toString()
                  }
                  required
                  rules={[{ required: true }]}
                  label="Service: "
                  options={
                    isNew
                      ? productList
                          ?.filter((product) => {
                            return !selectedProducts
                              ?.filter((item) => item.id?.toString() !== productIndex)
                              ?.map((selected) => selected.productId)
                              .includes(product.ID);
                          })
                          .filter((product) => !(isGeekcoverMember && product.ID === additional30MinChargeHome))
                          .map((product) => ({
                            label: product.name,
                            value: product.ID,
                            details: product,
                          }))
                      : [
                          {
                            label: selectedProduct?.description,
                            value: selectedProductId,
                            details: selectedProduct,
                          },
                        ]
                  }
                />
              </div>
              {selectedProduct && (
                <div className={styles.serviceList__item}>
                  <InputNumber.$
                    label="Qty: "
                    required
                    rules={[{ required: true }]}
                    name="productQty"
                    initialValue={
                      selectedProducts?.find((product) => product.id === productIndex)?.quantity?.toString() || '1'
                    }
                    min={1}
                    width={55}
                    step={1}
                    className={styles.serviceList__hours}
                    disabled={!selectedProduct.duration}
                    onKeyDown={(e) => e.preventDefault()}
                  />
                  <DatePicker.$
                    disabled={selectedProduct.restrict_display_in_booking === '1'}
                    label="Date: "
                    required
                    rules={[{ required: true }]}
                    // @ts-expect-error Type 'Date | null | undefined' is not assignable to type 'string | undefined'.
                    initialValue={
                      (productIndex === null
                        ? defaultCompletionDate
                        : selectedProducts?.[productIndex].completionDate) ?? new Date()
                    }
                    name={'productDate'}
                    allowClear={false}
                  />
                  {selectedProductId && selectedProductId !== null && (
                    <InputText.$
                      disabled
                      label="Unit Price: "
                      required
                      rules={[{ required: true }]}
                      name="productPrice"
                      initialValue={`$${Number(selectedProduct?.total).toFixed(2)} inc GST`}
                      min={1}
                      width={150}
                    />
                  )}
                  {selectedProductId && selectedProductId !== null && (
                    <InputText.$
                      disabled
                      label="Total Hours: "
                      required
                      rules={[{ required: true }]}
                      name="productDuration"
                      initialValue={`${calculateTotalHours([{ details: { product: selectedProduct }, qty: qty?.toString() }]) || '-'}`}
                      width={55}
                    />
                  )}
                </div>
              )}
            </div>
          </Form.Item>
        </Space.$>
      </Form>
    </Modal.$>
  );
};
